/**
 * 来場者向けトースト通知コンポーネント
 * お知らせ、ポイント変動、スタンプ獲得を画面上部に一時表示します
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Platform, Pressable, Text, View } from '../../../shared/components/WebPrimitives';

/** トースト表示時間（ミリ秒） */
const TOAST_DISPLAY_DURATION_MS = 5000;

/** QR読み取り・ポイント取得トースト表示時間（ミリ秒） */
const QR_TOAST_DISPLAY_DURATION_MS = 9000;

/** 成功演出トースト表示時間（ミリ秒） */
const CELEBRATION_TOAST_DISPLAY_DURATION_MS = 3200;

/** アニメーション時間（ミリ秒） */
const TOAST_ANIMATION_DURATION_MS = 300;

/** WebでのuseNativeDriver使用可否 */
const SHOULD_USE_NATIVE_DRIVER = Platform.OS !== 'web';

/**
 * 来場者向けトースト通知コンポーネント
 * @param {Object} props - プロパティ
 * @param {Object | null} props.notice - 表示する通知（{title, body, kind?, isImportant?}）
 * @param {() => void} [props.onPress] - タップ時コールバック
 * @param {() => void} [props.onDismiss] - 非表示時コールバック
 * @returns {JSX.Element | null} トースト通知
 */
const NoticeToast = ({ notice, onPress = undefined, onDismiss = undefined }) => {
  /** 表示中フラグ */
  const [isVisible, setIsVisible] = useState(false);

  /** アニメーション値 */
  const translateY = useRef(new Animated.Value(-100)).current;

  /** 成功演出の拡大率 */
  const scale = useRef(new Animated.Value(1)).current;

  /** 成功演出の透明度 */
  const opacity = useRef(new Animated.Value(0)).current;

  /** 自動非表示タイマー */
  const dismissTimerRef = useRef(null);

  /** スタンプトーストか */
  const isStampToast = notice?.kind === 'stamp';

  /** ポイントトーストか */
  const isPointToast = notice?.kind === 'point';

  /** スキャン読み取りトーストか */
  const isScanToast = notice?.kind === 'scan';

  /** ポイント取得成功トーストか */
  const isClaimToast = notice?.kind === 'claim';

  /** 大きく見せる成功演出トーストか */
  const isCelebrationToast = notice?.kind === 'celebration' || isClaimToast;

  /** 重要なお知らせトーストか */
  const isImportantNotice = notice?.kind === 'notice' && Boolean(notice?.isImportant);

  /**
   * トーストを非表示にする
   * @returns {void}
   */
  const handleDismiss = useCallback(() => {
    Animated.parallel([
      Animated.timing(translateY, {
        toValue: isCelebrationToast ? -28 : -100,
        duration: TOAST_ANIMATION_DURATION_MS,
        useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
      }),
      Animated.timing(scale, {
        toValue: isCelebrationToast ? 0.92 : 1,
        duration: TOAST_ANIMATION_DURATION_MS,
        useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
      }),
      Animated.timing(opacity, {
        toValue: 0,
        duration: TOAST_ANIMATION_DURATION_MS,
        useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
      }),
    ]).start(() => {
      setIsVisible(false);
      onDismiss?.();
    });
  }, [isCelebrationToast, onDismiss, opacity, scale, translateY]);

  useEffect(() => {
    if (notice) {
      translateY.setValue(isCelebrationToast ? -28 : -100);
      scale.setValue(isCelebrationToast ? 0.88 : 1);
      opacity.setValue(0);
      setIsVisible(true);

      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration: TOAST_ANIMATION_DURATION_MS,
          useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
        }),
        Animated.timing(scale, {
          toValue: 1,
          duration: TOAST_ANIMATION_DURATION_MS,
          useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: TOAST_ANIMATION_DURATION_MS,
          useNativeDriver: SHOULD_USE_NATIVE_DRIVER,
        }),
      ]).start();

      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }

      /** 通常より長く見せるトーストか */
      const shouldUseLongDisplay = notice.kind === 'scan' || notice.kind === 'claim';

      /** 表示時間 */
      const displayDuration = isCelebrationToast
        ? CELEBRATION_TOAST_DISPLAY_DURATION_MS
        : shouldUseLongDisplay
          ? QR_TOAST_DISPLAY_DURATION_MS
          : TOAST_DISPLAY_DURATION_MS;

      dismissTimerRef.current = setTimeout(() => {
        handleDismiss();
      }, displayDuration);
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [handleDismiss, isCelebrationToast, notice, opacity, scale, translateY]);

  if (!isVisible || !notice) {
    return null;
  }

  return (
    <Animated.View
      className={`absolute z-50 ${isCelebrationToast ? 'left-2 right-2 top-6 items-center' : 'left-2 right-2 -top-1'}`}
      style={{ opacity, transform: [{ translateY }, { scale }] }}
    >
      <Pressable disabled={!onPress} onPress={onPress}>
        <View
          className={`${isCelebrationToast ? 'w-full max-w-[360px] border-4 p-4 text-center shadow-[6px_6px_0_rgba(22,36,58,0.35)]' : 'border-2 p-3 shadow-lg'} rounded-none ${
            isImportantNotice
              ? 'border-[#DC2626] bg-[#FFF5F5]'
              : isStampToast
              ? 'border-app-quest-gold bg-[#FFF4C2]'
              : isPointToast
                ? 'border-app-primary bg-[#EAF6FF]'
                : isClaimToast
                  ? 'border-app-quest-gold bg-[#FFF4C2]'
              : isScanToast
                  ? 'border-app-quest-night bg-[#EAF6FF]'
                : 'border-app-quest-gold bg-[#FFF8DD]'
          }`}
        >
          <Text
            className={`${isCelebrationToast ? 'text-sm tracking-[2px]' : 'text-xs'} font-bold ${
              isImportantNotice || isScanToast ? 'text-app-quest-night' : 'text-app-quest-gold'
            }`}
          >
            {isStampToast
              ? 'NEW STAMP'
              : isPointToast
                ? 'POINT UPDATE'
                : isClaimToast
                  ? 'POINT GET'
                  : isScanToast
                    ? 'QR 読み取り完了'
                    : isImportantNotice
                      ? '重要なお知らせ'
                      : isCelebrationToast
                        ? 'SUCCESS'
                      : 'NEW お知らせ'}
          </Text>
          <Text
            className={`mt-1 ${isCelebrationToast ? 'text-2xl' : 'text-sm'} font-extrabold ${
              isImportantNotice || isStampToast || isScanToast || isClaimToast || isCelebrationToast
                ? 'text-app-quest-night'
                : 'text-app-text'
            }`}
            numberOfLines={isStampToast || isScanToast || isClaimToast || isCelebrationToast ? 2 : 1}
          >
            {notice.title}
          </Text>
          <Text
            className={`mt-1 ${isCelebrationToast ? 'text-sm font-semibold' : 'text-xs'} ${
              isImportantNotice || isStampToast || isScanToast || isClaimToast || isCelebrationToast
                ? 'text-app-quest-night/80'
                : 'text-app-text-secondary'
            }`}
            numberOfLines={isStampToast || isScanToast || isClaimToast || isCelebrationToast ? 3 : 2}
          >
            {notice.body}
          </Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

export default NoticeToast;
